import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, AlertTriangle, Clock, Inbox, BellRing } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";

export function NotificationBell({ dark = false, variant = "desktop" }) {
  const { can, user } = useAuth();
  const t = useT();
  const [items, setItems] = useState([]);       // general backbone notifications
  const [labItems, setLabItems] = useState([]); // lab delay/conflict alerts
  const [waitItems, setWaitItems] = useState([]); // waiting-time overdue alerts
  const labEnabled = can("laboratory", "view");
  const waitEnabled = ["owner", "operations_manager", "coordination", "translator_cashier",
                       "medical_secretary", "supervising_dentist", "treating_dentist"].includes(user?.role);
  const seenArrivals = useRef(null);            // ids of arrival notifications seen so far

  // Subtle two-tone chime via Web Audio (no asset, no OS/lock-screen notification,
  // no patient detail spoken/exposed — safe on shared or locked devices).
  const playChime = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const start = ctx.currentTime;
      [880, 1174.66].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        osc.connect(gain);
        gain.connect(ctx.destination);
        const t0 = start + i * 0.14;
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.07, t0 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.26);
        osc.start(t0);
        osc.stop(t0 + 0.3);
      });
      setTimeout(() => ctx.close().catch(() => {}), 800);
    } catch { /* audio unavailable — silent */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      const list = Array.isArray(data) ? data : [];
      setItems(list);
      // Chime when a brand-new patient-arrival notification appears (not on first load).
      const arrivals = list.filter((n) => n.type === "patient_arrival");
      const ids = new Set(arrivals.map((a) => a.id));
      if (seenArrivals.current !== null) {
        const hasNew = arrivals.some((a) => !a.read && !seenArrivals.current.has(a.id));
        if (hasNew) playChime();
      }
      seenArrivals.current = ids;
    } catch { /* silent */ }
    if (labEnabled) {
      try {
        const { data } = await api.get("/lab/notifications");
        setLabItems(Array.isArray(data) ? data : []);
      } catch { /* silent */ }
    }
    if (waitEnabled) {
      try {
        const { data } = await api.get("/appointments/waiting-alerts");
        setWaitItems(Array.isArray(data) ? data : []);
      } catch { /* silent */ }
    }
  }, [labEnabled, waitEnabled, playChime]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((n) => !n.read).length + labItems.length + waitItems.length;

  const markRead = async (n) => {
    if (n.read) return;
    try { await api.post(`/notifications/${n.id}/read`); setItems((p) => p.map((x) => x.id === n.id ? { ...x, read: true } : x)); }
    catch { /* silent */ }
  };
  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setItems((p) => p.map((x) => ({ ...x, read: true }))); }
    catch { /* silent */ }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`notification-bell-${variant}`}
          className={`relative inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
            dark ? "text-white/70 hover:text-white hover:bg-white/10" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          }`}
          aria-label="Notifications"
        >
          <Bell className="h-[18px] w-[18px]" strokeWidth={2} />
          {unread > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-navy">{t("notif.title")}</p>
          {items.some((n) => !n.read) && (
            <button onClick={markAll} data-testid="notif-mark-all" className="text-xs text-blue-600 hover:underline">{t("notif.markAll")}</button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && labItems.length === 0 && waitItems.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notification-empty">
              <Inbox className="h-5 w-5 mx-auto mb-2 text-slate-300" /> {t("notif.allCaughtUp")}
            </div>
          ) : (
            <>
              {waitItems.map((w, idx) => (
                <div key={`wait-${w.appointment_id}`} data-testid={`waiting-alert-item-${idx}`}
                  className="flex gap-3 px-4 py-3 border-b border-slate-50 last:border-0 bg-amber-50/70">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                  <div className="min-w-0">
                    <p className="text-sm text-slate-700 leading-snug">
                      <b>{w.patient_name}</b> {w.waited_minutes} {t("notif.waitingMin")} {w.threshold_minutes} {t("ops.min")})
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{w.dentist_name || t("notif.unassigned")}{w.visit_number ? ` · ${t("notif.visit")} ${w.visit_number}` : ""}</p>
                  </div>
                </div>
              ))}
              {items.map((n, idx) => {
                const isArrival = n.type === "patient_arrival";
                return (
                <button key={n.id} onClick={() => markRead(n)} data-testid={`notification-item-${idx}`}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-50 last:border-0 ${n.read ? "opacity-60" : isArrival ? "bg-emerald-50/60" : "bg-blue-50/40"}`}>
                  {isArrival
                    ? <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" data-testid={`arrival-notification-${idx}`} />
                    : (!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-blue-500 shrink-0" />)}
                  <div className="min-w-0">
                    <p className="text-sm text-slate-700 leading-snug">{n.message}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                </button>
                );
              })}
              {labItems.map((n, idx) => {
                const isConflict = n.type === "deadline_conflict";
                const Icon = isConflict ? AlertTriangle : Clock;
                return (
                  <div key={`lab-${idx}`} data-testid={`lab-notification-item-${idx}`}
                    className="flex gap-3 px-4 py-3 border-b border-slate-50 last:border-0">
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${isConflict ? "text-amber-500" : "text-red-500"}`} />
                    <p className="text-sm text-slate-700 leading-snug">{n.message}</p>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
